import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { StatusPill, type StatusTone } from '@/components/ui/kit';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import { WorkspaceIcon } from '../icons';
import { tileText, useTimeLabel } from './format';
import type { SourceRowVM, SourceStatus, SourcesPanelVM } from './types';

// The Sources panel: where each number comes from and how fresh it is. It
// mounts in UI1's frame slot. A source the viewer may not see shows no name,
// system or locator: only that it exists and is hidden.

export interface SourcesPanelProps {
  vm: SourcesPanelVM;
  onRetry?: () => void;
  /** When given, a visible row opens its evidence and the whole row is the button. */
  onOpenEvidence?: (sourceId: string) => void;
}

export function SourcesPanel({ vm, onRetry, onOpenEvidence }: SourcesPanelProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const denied = vm.rows.filter((row) => row.status === 'denied').length;
  return (
    <section className="ap-panel" aria-labelledby="ap-sources-title" data-panel="sources">
      <header className="ap-head">
        <h2 id="ap-sources-title">{copy.sourcesTitle}</h2>
        <p>{copy.sourcesIntro}</p>
      </header>
      {vm.load === 'loading' && <LoadingState label={copy.sourcesLoading} />}
      {vm.load === 'error' && <ErrorState message={copy.sourcesErrorBody} onRetry={onRetry} />}
      {vm.load === 'ready' && vm.rows.length === 0 && (
        <EmptyState
          icon="source"
          title={copy.sourcesEmptyTitle}
          description={copy.sourcesEmptyBody}
        />
      )}
      {vm.load === 'ready' && vm.rows.length > 0 && (
        <>
          {denied > 0 && (
            <p className="ap-note" role="status">
              <strong>{copy.sourcesDeniedTitle}. </strong>
              {formatCopy(copy.sourcesDeniedBody, { count: String(denied) })}
            </p>
          )}
          <ul className="ap-list">
            {vm.rows.map((row) => (
              <SourceRow key={row.id} row={row} onOpen={onOpenEvidence} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const STATUS_KEY = {
  current: 'sourceStatusCurrent',
  stale: 'sourceStatusStale',
  gap: 'sourceStatusGap',
  denied: 'sourceStatusDenied',
} as const;
// Amber is "needs a look"; red is only for a source that failed or was denied.
const TONE: Record<SourceStatus, StatusTone> = {
  current: 'success',
  stale: 'warning',
  gap: 'warning',
  denied: 'danger',
};

export function SourceRow({ row, onOpen }: { row: SourceRowVM; onOpen?: (id: string) => void }) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const time = useTimeLabel();
  const denied = row.status === 'denied';
  const when = time(row.asOf);
  const body = (
    <>
      <span className="ap-tile" aria-hidden="true">
        {denied ? <WorkspaceIcon name="lock" size={16} /> : tileText(row.system)}
      </span>
      <span className="ap-row-copy">
        <strong>{denied ? copy.sourceDeniedName : (row.label ?? copy.sourceDeniedName)}</strong>
        {!denied && row.system && <span>{row.system}</span>}
        {!denied && (
          <span>
            {row.status === 'gap' && when
              ? formatCopy(copy.sourceLastGood, { time: when })
              : when
                ? formatCopy(copy.sourceAsOf, { time: when })
                : copy.sourceAgeUnknown}
          </span>
        )}
        {!denied && row.usedBy.length > 0 && (
          <span>{formatCopy(copy.sourceUsedBy, { labels: row.usedBy.join(', ') })}</span>
        )}
      </span>
      <StatusPill tone={TONE[row.status]}>{copy[STATUS_KEY[row.status]]}</StatusPill>
    </>
  );
  return (
    <li className="ap-row" data-status={row.status}>
      {onOpen && !denied ? (
        <button type="button" className="ap-row-button" onClick={() => onOpen(row.id)}>
          {body}
        </button>
      ) : (
        <div className="ap-row-button" data-static="true">
          {body}
        </div>
      )}
    </li>
  );
}
